
/*
	Original code: https://stackoverflow.com/questions/12578507/implement-an-input-with-a-mask
	Code traslated to Vue using Google Gemini
*/

export default {
	name: "MaskedInput",
	props: {
		// v-model value
		value: {
			type: String,
			default: "",
		},
		// The mask pattern (e.g., "dd/mm/yyyy")
		placeholder: {
			type: String,
			required: true,
		},
		// Characters that act as placeholders in the mask (e.g., "_" or "dmyh")
		slotChar: {
			type: String,
			default: "_", // Maps to data-slots
		},
		// Regex string for allowed input characters (e.g., "\d")
		accept: {
			type: String,
			default: "\\d", // Maps to data-accept
		},
		// Standard HTML size attribute
		size: {
			type: [String, Number],
			default: null,
		},
	},
	data() {
		return {
			back: false, // Internal state to track backspace usage
		};
	},
	computed: {
		// Create a Set for O(1) lookup of slot characters
		slotsSet() {
			return new Set(this.slotChar.split(""));
		},
		// Regex instance for cleaning input
		acceptRegEx() {
			return new RegExp(this.accept, "g");
		},
		// Calculates the array of previous valid cursor positions
		prev() {
			let j = 0;
			return Array.from(this.placeholder, (c, i) => {
				if (this.slotsSet.has(c)) {
					j = i + 1;
				}
				return j;
			});
		},
		// Finds the index of the first editable character
		first() {
			return [...this.placeholder].findIndex((c) => this.slotsSet.has(c));
		},
	},
	methods: {
		/**
		 * Core logic to merge user input with the mask pattern
		 */
		clean(inputValue) {
			// 1. Create a temporary variable to hold the raw input
			let content = inputValue;
			
			// 2. Iterate through the placeholder (mask) to find static characters
			//    and remove them from the input content one by one.
			for (const c of this.placeholder) {
				// If this char is a slot, skip it (we want to keep slot data)
				if (this.slotsSet.has(c)) continue;
				
				// If it's a static character, remove its *first* occurrence from the input.
				// This effectively "peels off" the mask structure, leaving only user input.
				// We use replace() which only removes the first match, preserving order.
				content = content.replace(c, ""); 
			}
			
			// 3. Now extract the remaining characters that match the accept regex
			const inputMatches = content.match(this.acceptRegEx) || [];

			// 4. Rebuild the string using the pattern and the clean user input
			return Array.from(this.placeholder, (c) => {
				// If the current pattern char is a slot...
				if (this.slotsSet.has(c)) {
						// ...use the next available user input character
						return inputMatches.shift() || c;
				}
				// Otherwise, return the static mask character
				return c;
			});
		},

		format(e) {
			const el = e.target;
			
			// 1. Calculate Cursor Position
			// We process clean() on the substring up to the cursor to find where the cursor should jump
			const [i, j] = [el.selectionStart, el.selectionEnd].map((pos) => {
				const cleanedPart = this.clean(el.value.slice(0, pos));
				// Find the index of the last filled slot in this slice
				const foundIndex = cleanedPart.findIndex((c) => this.slotsSet.has(c));
				
				if (foundIndex < 0) return this.prev.at(-1); // End of input
				
				// Logic for Backspace vs Normal input
				return this.back ? (this.prev[foundIndex - 1] || this.first) : foundIndex;
			});

			// 2. Update Value
			const formattedValue = this.clean(el.value).join("");
			el.value = formattedValue;
			
			// 3. Set Cursor
			el.setSelectionRange(i, j);
			
			// 4. Reset Backspace flag
			this.back = false;

			// 5. Emit to parent (v-model update)
			// Only emit if value actually changed to prevent infinite loops
			if (formattedValue !== this.value) {
				this.$emit("input", formattedValue);
			}
		},

		onKeydown(e) {
			this.back = e.key === "Backspace";
			if (this.$listeners.keydown) this.$listeners.keydown(e);
		},

		onInput(e) {
			this.format(e);
		},

		onFocus(e) {
			this.format(e);
			if (this.$listeners.focus) this.$listeners.focus(e);
		},

		onBlur(e) {
			const el = e.target;
			// If the value equals the placeholder (mask is empty), clear it out
			if (el.value === this.placeholder) {
				el.value = "";
				this.$emit("input", "");
			}

			if (this.$listeners.blur) this.$listeners.blur(e);
		},

		onChange(e) {
			if (this.$listeners.change) {
				// Create a synthetic event or pass the current one
				// Note: e.target.value is already updated
				this.$listeners.change(e);
			}
		},
	},
};
